import { styled } from '@mui/material';

import {
  CheckCircleIcon,
  DocumentIcon,
  ForwardIcon,
  RefreshIcon,
} from '../../../icons';
import { Button } from '../../atoms/Button';
import { Chip } from '../../atoms/Chip';
import { Stack } from '../../atoms/Stack';
import { Tag } from '../../atoms/Tag';
import { Typography } from '../../atoms/Typography';
import { Card } from '../Card';
import { CardActions } from '../CardActions';
import { CardContent } from '../CardContent';
import { HTMLDisplay } from '../HTMLDisplay';
import { ResourceMenuActions } from '../../organisms/ResourceMenuActions';
import {
  type IncidentCardAction,
  type IncidentCardProps,
  type IncidentDocument,
  type IncidentItem,
  type IncidentStatus,
  useIncidentCard,
} from './useIncidentCard';

type Props = IncidentCardProps;

const HeaderContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: theme.spacing(1),
}));

const HeaderMeta = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: theme.spacing(1),
  minWidth: 0,
  flex: 1,
}));

const HeaderActions = styled('div')(({ theme }) => ({
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
}));

const TitleText = styled(Typography)({
  wordBreak: 'break-word',
});

export function IncidentCard(props: Props) {
  const hook = useIncidentCard(props);

  return (
    <Card data-testid={props['data-testid']}>
      <CardContent>
        <Stack spacing={1}>
          <HeaderContainer>
            <HeaderMeta>
              {hook.isResolved ? (
                <Tag
                  color="done"
                  label="Terminé"
                  variantIcon='state'
                />
              ) : (
                <Tag
                  color="incident"
                  label="En cours"
                  variantIcon='state'
                />
              )}

              {hook.formattedDate ? (
                <Typography variant="caption">
                  Signalé le {hook.formattedDate}
                </Typography>
              ) : null}
            </HeaderMeta>

            <HeaderActions>
              {props.dragHandle}
              {props.action ? (
                props.action
              ) : hook.hasActions ? (
                <ResourceMenuActions
                  size="small"
                  actions={hook.actions}
                  tooltip={hook.actionsButtonTooltip}
                  ariaLabel={hook.actionsButtonAriaLabel}
                />
              ) : null}
            </HeaderActions>
          </HeaderContainer>

          <TitleText variant="body2Emphasis">
            {props.incident.title}
          </TitleText>

          {hook.providerName || hook.contactName ? (
            <Stack direction="row" spacing={1} alignItems="center" wrap="wrap">
              {hook.providerName ? (
                <Typography variant="caption">
                  Prestataire : <strong>{hook.providerName}</strong>
                </Typography>
              ) : null}
              {hook.contactName ? (
                <Typography variant="caption">
                  Contact : <strong>{hook.contactName}</strong>
                </Typography>
              ) : null}
            </Stack>
          ) : null}

          {hook.hasComment ? (
            <HTMLDisplay
              content={props.incident.comment!}
              maxLines={props.maxCommentLines ?? 3}
            />
          ) : null}

          {hook.hasDocuments ? (
            <Stack direction="row" spacing={1} alignItems="center" wrap="wrap">
              {hook.documents.map((doc, index) => {
                const docUrl = doc.url || doc.file_url;
                if (docUrl) {
                  return (
                    <Chip
                      key={doc.id ?? index}
                      icon={<DocumentIcon fontSize="small" />}
                      label={doc.name}
                      href={docUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="small"
                      variant="outlined"
                    />
                  );
                }

                return (
                  <Tag
                    key={doc.id ?? index}
                    color="default"
                    label={doc.name}
                    icon={<DocumentIcon fontSize="small" />}
                  />
                );
              })}
            </Stack>
          ) : null}
        </Stack>
      </CardContent>

      {hook.hasFooterActions ? (
        <CardActions>
          {props.footerActions ? (
            props.footerActions
          ) : props.onNavigate ? (
            <Button
              size="small"
              variant="text"
              endIcon={<ForwardIcon fontSize="small" />}
              onClick={hook.handleNavigate}
            >
              {props.navigateLabel ?? "Consulter l'incident"}
            </Button>
          ) : (props.onToggleStatus || props.onStatusChange) ? (
            hook.isResolved ? (
              <Button
                size="small"
                variant="text"
                color="default"
                startIcon={<RefreshIcon fontSize="small" />}
                onClick={hook.handleToggleStatus}
              >
                Repasser en cours
              </Button>
            ) : (
              <Button
                size="small"
                variant="contained"
                color="success"
                startIcon={<CheckCircleIcon fontSize="small" />}
                onClick={hook.handleToggleStatus}
              >
                Marquer comme terminé
              </Button>
            )
          ) : null}
        </CardActions>
      ) : null}
    </Card>
  );
}

export type {
  IncidentCardAction,
  IncidentCardProps,
  IncidentDocument,
  IncidentItem,
  IncidentStatus,
};
